import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/auth";
import { maskCep } from "../../../utils/mask";
import { maskMoney } from "../../../utils/mask";
import { IProponente } from "../types/proposta";
import {
  mapPortalClientToProponenteFields,
  PortalClientResponse,
} from "../utils/mapPortalClientToProponente";
import {
  buildLinkedCpfDigits,
  isCompleteDocument,
  normalizeCpfDigits,
  resolveIdClienteForSubmit,
} from "../utils/proponenteIdentity";
import { proponenteToPhoneInput, phoneInputToProponente } from "../utils/phoneHelpers";

export type LookupStatus = "idle" | "loading" | "found" | "not_found" | "error";

export type ProponenteFormState = {
  nome: string;
  cpfCnpj: string;
  rg: string;
  emissorRG: string;
  dataNascimento: string;
  naturalidade: string;
  nacionalidade: string;
  estadoCivil: string;
  telefone: string;
  cod_pais: string;
  phoneInput: string;
  email: string;
  profissao: string;
  rendaMensal: string;
  observacoes: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  codibge: string;
  bairro: string;
  estado: string;
  cidade: string;
  id_cliente?: number;
  id_pessoa?: number;
  linkedCpfDigits: string | null;
  lookupStatus: LookupStatus;
};

export const EMPTY_PROPONENTE_FORM: ProponenteFormState = {
  nome: "",
  cpfCnpj: "",
  rg: "",
  emissorRG: "",
  dataNascimento: "",
  naturalidade: "",
  nacionalidade: "",
  estadoCivil: "",
  telefone: "",
  cod_pais: "+55",
  phoneInput: "",
  email: "",
  profissao: "",
  rendaMensal: "",
  observacoes: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  codibge: "",
  bairro: "",
  estado: "",
  cidade: "",
  id_cliente: undefined,
  id_pessoa: undefined,
  linkedCpfDigits: null,
  lookupStatus: "idle",
};

function hydrateFromProponente(proponente?: IProponente): ProponenteFormState {
  if (!proponente || (!proponente.nome && !proponente.cpfCnpj)) {
    return { ...EMPTY_PROPONENTE_FORM };
  }
  const cpfCnpj = proponente.cpfCnpj ?? "";
  const linkedCpfDigits = buildLinkedCpfDigits(cpfCnpj, proponente.id_cliente);
  return {
    nome: proponente.nome ?? "",
    cpfCnpj,
    rg: proponente.rg ?? "",
    emissorRG: proponente.emissorRG ?? "",
    dataNascimento: proponente.dataNascimento ?? "",
    naturalidade: proponente.naturalidade ?? "",
    nacionalidade: proponente.nacionalidade ?? "",
    estadoCivil: proponente.estadoCivil ?? "",
    telefone: proponente.telefone ?? "",
    cod_pais: proponente.cod_pais ?? "+55",
    phoneInput:
      proponenteToPhoneInput(proponente.telefone, proponente.cod_pais) || "",
    email: proponente.email ?? "",
    profissao: proponente.profissao ?? "",
    rendaMensal: proponente.rendaMensal
      ? proponente.rendaMensal.includes("R$")
        ? proponente.rendaMensal
        : maskMoney(String(Math.round(Number(proponente.rendaMensal) * 100)))
      : "",
    observacoes: proponente.observacoes ?? "",
    cep: proponente.cep ? maskCep(proponente.cep) : "",
    logradouro: proponente.logradouro ?? "",
    numero: proponente.numero ?? "",
    complemento: proponente.complemento ?? "",
    codibge: proponente.codibge ?? "",
    bairro: proponente.bairro ?? "",
    estado: proponente.estado ?? "",
    cidade: proponente.cidade ?? "",
    id_cliente: proponente.id_cliente,
    id_pessoa: proponente.id_pessoa,
    linkedCpfDigits,
    lookupStatus: "idle",
  };
}

export function useProponenteForm(
  proponente?: IProponente,
  isEditing?: boolean,
) {
  const { api } = useAuth();
  const [form, setForm] = useState<ProponenteFormState>(() =>
    hydrateFromProponente(proponente),
  );
  const lastLookedUpCpfRef = useRef<string | null>(null);
  const lookupAbortRef = useRef<AbortController | null>(null);
  const lastSearchedCepRef = useRef("");
  const isHydratingRef = useRef(false);

  useEffect(() => {
    isHydratingRef.current = true;
    setForm(hydrateFromProponente(proponente));
    lastLookedUpCpfRef.current = proponente?.cpfCnpj
      ? normalizeCpfDigits(proponente.cpfCnpj)
      : null;
    lastSearchedCepRef.current = "";
    const timer = setTimeout(() => {
      isHydratingRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [proponente, isEditing]);

  const lookupClientByCpf = useCallback(
    async (cpfDigits: string, cpfCnpjMasked: string) => {
      if (!isCompleteDocument(cpfDigits)) return;
      if (lastLookedUpCpfRef.current === cpfDigits) return;

      lookupAbortRef.current?.abort();
      const controller = new AbortController();
      lookupAbortRef.current = controller;

      setForm((prev) => ({ ...prev, lookupStatus: "loading" }));

      try {
        const res = await api.get(`/portal/client/${cpfDigits}`, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const data = (
          Array.isArray(res.data) ? res.data[0] : res.data?.data?.[0] ?? res.data
        ) as PortalClientResponse | undefined;

        if (!data || !data.nome) {
          lastLookedUpCpfRef.current = cpfDigits;
          setForm((prev) => {
            if (normalizeCpfDigits(prev.cpfCnpj) !== cpfDigits) return prev;
            return {
              ...EMPTY_PROPONENTE_FORM,
              cpfCnpj: cpfCnpjMasked,
              lookupStatus: "not_found",
            };
          });
          return;
        }

        const mapped = mapPortalClientToProponenteFields(data, cpfCnpjMasked);
        lastLookedUpCpfRef.current = cpfDigits;

        setForm((prev) => {
          if (normalizeCpfDigits(prev.cpfCnpj) !== cpfDigits) return prev;
          const phoneInput =
            proponenteToPhoneInput(mapped.telefone, mapped.cod_pais) || "";
          return {
            ...prev,
            ...mapped,
            cpfCnpj: mapped.cpfCnpj ?? cpfCnpjMasked,
            phoneInput,
            rendaMensal: mapped.rendaMensal || prev.rendaMensal,
            linkedCpfDigits: mapped.id_cliente ? cpfDigits : null,
            lookupStatus: "found",
          };
        });
      } catch (error: unknown) {
        const err = error as { code?: string; response?: { status?: number } };
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        lastLookedUpCpfRef.current = cpfDigits;
        setForm((prev) => {
          if (normalizeCpfDigits(prev.cpfCnpj) !== cpfDigits) return prev;
          if (err?.response?.status === 404) {
            return {
              ...EMPTY_PROPONENTE_FORM,
              cpfCnpj: cpfCnpjMasked,
              lookupStatus: "not_found",
            };
          }
          return {
            ...prev,
            id_cliente: undefined,
            id_pessoa: undefined,
            linkedCpfDigits: null,
            lookupStatus: "error",
          };
        });
      } finally {
        if (lookupAbortRef.current === controller) {
          lookupAbortRef.current = null;
        }
      }
    },
    [],
  );

  const setField = useCallback(
    <K extends keyof ProponenteFormState>(
      key: K,
      value: ProponenteFormState[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleCpfCnpjChange = useCallback(
    (maskedValue: string) => {
      const nextDigits = normalizeCpfDigits(maskedValue);
      setForm((prev) => {
        const previousDigits = normalizeCpfDigits(prev.cpfCnpj);
        if (previousDigits !== nextDigits && previousDigits.length > 0) {
          lastLookedUpCpfRef.current = null;
          return {
            ...EMPTY_PROPONENTE_FORM,
            cpfCnpj: maskedValue,
            lookupStatus: "idle",
          };
        }
        return {
          ...prev,
          cpfCnpj: maskedValue,
          lookupStatus: "idle",
          id_cliente: undefined,
          id_pessoa: undefined,
          linkedCpfDigits: null,
        };
      });
      if (!isHydratingRef.current && isCompleteDocument(nextDigits)) {
        lookupClientByCpf(nextDigits, maskedValue);
      }
    },
    [lookupClientByCpf],
  );

  const handleCpfCnpjBlur = useCallback(() => {
    const cpfDigits = normalizeCpfDigits(form.cpfCnpj);
    if (isCompleteDocument(cpfDigits)) {
      lookupClientByCpf(cpfDigits, form.cpfCnpj);
    }
  }, [form.cpfCnpj, lookupClientByCpf]);

  const handlePhoneChange = useCallback((value?: string) => {
    setForm((prev) => ({
      ...prev,
      phoneInput: value || "",
    }));
  }, []);

  useEffect(() => {
    const cepDigits = form.cep.replace(/\D/g, "");
    if (cepDigits.length !== 8) {
      if (cepDigits.length === 0) lastSearchedCepRef.current = "";
      return;
    }
    if (lastSearchedCepRef.current === cepDigits) return;

    let isActive = true;
    const fetchCep = async () => {
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cepDigits}/json/`,
        );
        const data = await response.json();
        if (!isActive || data?.erro) return;
        lastSearchedCepRef.current = cepDigits;
        setForm((prev) => ({
          ...prev,
          logradouro: data.logradouro ?? prev.logradouro,
          bairro: data.bairro ?? prev.bairro,
          cidade: data.localidade ?? prev.cidade,
          estado: data.uf ?? prev.estado,
          codibge: data.ibge ?? prev.codibge,
        }));
      } catch {
        // ignore
      }
    };
    fetchCep();
    return () => {
      isActive = false;
    };
  }, [form.cep]);

  const buildProponenteForSave = useCallback((): IProponente => {
    const id_cliente = resolveIdClienteForSubmit({
      id_cliente: form.id_cliente,
      linkedCpfDigits: form.linkedCpfDigits,
      cpfCnpj: form.cpfCnpj,
    });

    const { telefone, cod_pais } = phoneInputToProponente(form.phoneInput);

    return {
      ...(id_cliente ? { id_cliente } : {}),
      ...(form.id_pessoa && id_cliente ? { id_pessoa: form.id_pessoa } : {}),
      nome: form.nome,
      cpfCnpj: form.cpfCnpj,
      rg: form.rg,
      emissorRG: form.emissorRG,
      dataNascimento: form.dataNascimento,
      naturalidade: form.naturalidade,
      nacionalidade: form.nacionalidade,
      estadoCivil: form.estadoCivil,
      telefone,
      cod_pais,
      email: form.email,
      profissao: form.profissao,
      rendaMensal: form.rendaMensal,
      observacoes: form.observacoes,
      cep: form.cep,
      logradouro: form.logradouro,
      numero: form.numero,
      complemento: form.complemento,
      bairro: form.bairro,
      estado: form.estado,
      cidade: form.cidade,
      codibge: form.codibge,
    };
  }, [form]);

  const resetForm = useCallback(() => {
    lookupAbortRef.current?.abort();
    lookupAbortRef.current = null;
    lastLookedUpCpfRef.current = null;
    lastSearchedCepRef.current = "";
    setForm({ ...EMPTY_PROPONENTE_FORM });
  }, []);

  return {
    form,
    setField,
    handleCpfCnpjChange,
    handleCpfCnpjBlur,
    handlePhoneChange,
    buildProponenteForSave,
    resetForm,
    lookupStatus: form.lookupStatus,
  };
}
